import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import { ArrowUpDown, Check, CheckCheck, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function FeedbackDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all"); // all, pending, reviewed
  const [editingAnswerId, setEditingAnswerId] = useState(null);
  const [inlineFeedback, setInlineFeedback] = useState({});
  const [sortBy, setSortBy] = useState("date_desc"); // date_desc, date_asc, name_asc, name_desc, status
  const [selectedAnswers, setSelectedAnswers] = useState(new Set());
  const [bulkFeedback, setBulkFeedback] = useState("");
  const [showBulkModal, setShowBulkModal] = useState(false);

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(response.data);
      if (response.data.length > 0) {
        loadClassAnswers(response.data[0].id);
        setSelectedClass(response.data[0]);
      }
    } catch (err) {
      console.error("Error loading classes:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadClassAnswers = async (classId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/classes/${classId}/answers`);
      setAnswers(response.data);
    } catch (err) {
      console.error("Error loading answers:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (cls) => {
    setSelectedClass(cls);
    loadClassAnswers(cls.id);
  };

  const handleSubmitFeedback = async () => {
    if (!feedback.trim() || !selectedAnswer) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/answers/${selectedAnswer.id}/feedback`, {
        feedback: feedback.trim()
      });
      
      // Reload answers
      await loadClassAnswers(selectedClass.id);
      setSelectedAnswer(null);
      setFeedback("");
      alert("Feedback submitted successfully! 🎉");
    } catch (err) {
      console.error("Error submitting feedback:", err);
      alert("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const getFilteredAnswers = () => {
    let filtered = answers;
    
    switch (filterStatus) {
      case "pending":
        filtered = answers.filter(a => !a.feedback);
        break;
      case "reviewed":
        filtered = answers.filter(a => a.feedback);
        break;
      default:
        filtered = answers;
    }
    
    // Apply sorting
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case "date_asc":
          return new Date(a.submitted_at) - new Date(b.submitted_at);
        case "name_asc":
          return (a.student_name || "").localeCompare(b.student_name || "");
        case "name_desc":
          return (b.student_name || "").localeCompare(a.student_name || "");
        case "status":
          // Pending first, then reviewed
          if (!a.feedback && b.feedback) return -1;
          if (a.feedback && !b.feedback) return 1;
          return new Date(b.submitted_at) - new Date(a.submitted_at);
        case "date_desc":
        default:
          return new Date(b.submitted_at) - new Date(a.submitted_at);
      }
    });
  };

  const filteredAnswers = getFilteredAnswers();

  // Bulk selection handlers
  const handleSelectAnswer = (answerId) => {
    const newSelected = new Set(selectedAnswers);
    if (newSelected.has(answerId)) {
      newSelected.delete(answerId);
    } else {
      newSelected.add(answerId);
    }
    setSelectedAnswers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedAnswers.size === filteredAnswers.length) {
      setSelectedAnswers(new Set());
    } else {
      setSelectedAnswers(new Set(filteredAnswers.map(a => a.id)));
    }
  };

  const handleBulkFeedback = async () => {
    if (!bulkFeedback.trim() || selectedAnswers.size === 0) return;
    
    setSubmitting(true);
    try {
      // Submit feedback for each selected answer
      await Promise.all(
        Array.from(selectedAnswers).map(answerId =>
          axios.post(`${API}/answers/${answerId}/feedback`, {
            feedback: bulkFeedback.trim()
          })
        )
      );
      
      await loadClassAnswers(selectedClass.id);
      setSelectedAnswers(new Set());
      setBulkFeedback("");
      setShowBulkModal(false);
      alert(`Feedback added to ${selectedAnswers.size} answers! 🎉`);
    } catch (err) {
      console.error("Error submitting bulk feedback:", err);
      alert("Failed to submit some feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInlineEdit = (answerId, currentFeedback) => {
    setEditingAnswerId(answerId);
    setInlineFeedback({ ...inlineFeedback, [answerId]: currentFeedback || "" });
  };

  const handleSaveInlineFeedback = async (answerId) => {
    try {
      await axios.post(`${API}/answers/${answerId}/feedback`, {
        feedback: inlineFeedback[answerId]
      });
      await loadClassAnswers(selectedClass.id);
      setEditingAnswerId(null);
      alert("Feedback saved successfully! 🎉");
    } catch (err) {
      console.error("Error saving feedback:", err);
      alert("Failed to save feedback. Please try again.");
    }
  };

  const handleCancelInlineEdit = () => {
    setEditingAnswerId(null);
  };

  if (loading && classes.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/dashboard")}
          className="mb-6 text-gray-600 hover:text-gray-800 font-semibold flex items-center gap-2"
          data-testid="back-to-dashboard-btn"
        >
          ← Back to Dashboard
        </button>

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2" data-testid="feedback-title">
            Student Feedback Center 📝
          </h1>
          <p className="text-gray-600">Review student answers and provide personalized feedback</p>
        </div>

        {/* Class Selector */}
        {classes.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Select Class</h2>
            <div className="flex flex-wrap gap-3">
              {classes.map((cls) => (
                <button
                  key={cls.id}
                  onClick={() => handleClassChange(cls)}
                  className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                    selectedClass?.id === cls.id
                      ? "bg-green-500 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                  data-testid={`class-selector-${cls.id}`}
                >
                  {cls.name} ({answers.filter(a => true).length})
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex items-center gap-4">
            <span className="font-semibold text-gray-700">Filter:</span>
            <button
              onClick={() => setFilterStatus("all")}
              className={`px-4 py-2 rounded-lg font-semibold ${
                filterStatus === "all" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"
              }`}
              data-testid="filter-all"
            >
              All ({answers.length})
            </button>
            <button
              onClick={() => setFilterStatus("pending")}
              className={`px-4 py-2 rounded-lg font-semibold ${
                filterStatus === "pending" ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-700"
              }`}
              data-testid="filter-pending"
            >
              Pending Review ({answers.filter(a => !a.feedback).length})
            </button>
            <button
              onClick={() => setFilterStatus("reviewed")}
              className={`px-4 py-2 rounded-lg font-semibold ${
                filterStatus === "reviewed" ? "bg-green-500 text-white" : "bg-gray-200 text-gray-700"
              }`}
              data-testid="filter-reviewed"
            >
              Reviewed ({answers.filter(a => a.feedback).length})
            </button>
          </div>

          {/* Sorting */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-200">
            <span className="font-semibold text-gray-700 flex items-center gap-1">
              <ArrowUpDown className="w-4 h-4" /> Sort:
            </span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-lg font-medium text-gray-700 focus:outline-none focus:border-green-400"
              data-testid="sort-select"
            >
              <option value="date_desc">Newest First</option>
              <option value="date_asc">Oldest First</option>
              <option value="name_asc">Student Name (A-Z)</option>
              <option value="name_desc">Student Name (Z-A)</option>
              <option value="status">Status (Pending First)</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedAnswers.size > 0 && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCheck className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-800">
                {selectedAnswers.size} answer{selectedAnswers.size > 1 ? "s" : ""} selected
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowBulkModal(true)}
                className="bg-green-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center gap-2"
                data-testid="bulk-feedback-btn"
              >
                <Check className="w-4 h-4" />
                Add Feedback to Selected
              </button>
              <button
                onClick={() => setSelectedAnswers(new Set())}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-300 transition-colors flex items-center gap-2"
                data-testid="clear-selection-btn"
              >
                <X className="w-4 h-4" />
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {/* Answers List */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Student Answers</h2>
            {filteredAnswers.length > 0 && (
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                data-testid="select-all-btn"
              >
                {selectedAnswers.size === filteredAnswers.length ? (
                  <>
                    <X className="w-4 h-4" /> Deselect All
                  </>
                ) : (
                  <>
                    <CheckCheck className="w-4 h-4" /> Select All ({filteredAnswers.length})
                  </>
                )}
              </button>
            )}
          </div>

          {filteredAnswers.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📝</div>
              <p className="text-gray-600">
                {filterStatus === "pending" ? "No pending answers to review" : 
                 filterStatus === "reviewed" ? "No reviewed answers yet" :
                 "No student answers yet"}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAnswers.map((answer) => (
                <div
                  key={answer.id}
                  className={`border-2 rounded-lg p-6 transition-colors ${
                    selectedAnswers.has(answer.id) 
                      ? "border-blue-400 bg-blue-50" 
                      : "border-gray-200 hover:border-green-300"
                  }`}
                  data-testid={`answer-card-${answer.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3 flex-1">
                      {/* Checkbox for selection */}
                      <input
                        type="checkbox"
                        checked={selectedAnswers.has(answer.id)}
                        onChange={() => handleSelectAnswer(answer.id)}
                        className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        data-testid={`select-answer-${answer.id}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-gray-800">{answer.student_name}</h3>
                          <span className="text-sm text-gray-500">{answer.student_email}</span>
                        </div>
                        <div className="text-sm text-gray-600 mb-1">
                          <span className="font-semibold">{answer.module_title}</span> → {answer.lesson_title}
                        </div>
                        <div className="text-xs text-gray-500">
                          Submitted: {new Date(answer.submitted_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    {answer.feedback ? (
                      <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
                        ✓ Reviewed
                      </span>
                    ) : (
                      <span className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold">
                        Pending
                      </span>
                    )}
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <h4 className="font-semibold text-gray-700 mb-2">Student Answer:</h4>
                    <p className="text-gray-800 whitespace-pre-wrap">{answer.answer_text}</p>
                  </div>

                  {editingAnswerId === answer.id ? (
                    <div className="bg-blue-50 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Edit Feedback:</h4>
                      <textarea
                        value={inlineFeedback[answer.id] || ""}
                        onChange={(e) => setInlineFeedback({ ...inlineFeedback, [answer.id]: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-blue-300 rounded-lg focus:outline-none focus:border-blue-500 min-h-[100px]"
                        placeholder="Type your feedback here..."
                      />
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => handleSaveInlineFeedback(answer.id)}
                          className="educator-button px-4 py-2 rounded-lg text-white font-semibold"
                        >
                          Save Feedback
                        </button>
                        <button
                          onClick={handleCancelInlineEdit}
                          className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : answer.feedback ? (
                    <div className="bg-green-50 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold text-green-900 mb-2">Your Feedback:</h4>
                      <p className="text-green-800 whitespace-pre-wrap">{answer.feedback}</p>
                      <div className="text-xs text-green-700 mt-2">
                        Provided: {new Date(answer.feedback_at).toLocaleString()}
                      </div>
                    </div>
                  ) : null}

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleInlineEdit(answer.id, answer.feedback)}
                      className="educator-button px-6 py-2 rounded-lg text-white font-semibold"
                      data-testid={`edit-feedback-${answer.id}`}
                    >
                      {answer.feedback ? "Edit Feedback" : "Add Feedback"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      {selectedAnswer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedAnswer(null)}
        >
          <div
            className="bg-white rounded-2xl p-8 max-w-3xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                Provide Feedback for {selectedAnswer.student_name}
              </h2>
              <button
                onClick={() => setSelectedAnswer(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
                data-testid="close-feedback-modal"
              >
                ×
              </button>
            </div>

            <div className="mb-6">
              <div className="text-sm text-gray-600 mb-2">
                <span className="font-semibold">{selectedAnswer.module_title}</span> → {selectedAnswer.lesson_title}
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">Student Answer:</h4>
                <p className="text-gray-800 whitespace-pre-wrap">{selectedAnswer.answer_text}</p>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 font-semibold mb-2">Your Feedback:</label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500 min-h-[200px]"
                placeholder="Provide constructive feedback to help the student learn..."
                data-testid="feedback-textarea"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setSelectedAnswer(null)}
                className="flex-1 px-6 py-3 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
                data-testid="cancel-feedback-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitFeedback}
                disabled={submitting || !feedback.trim()}
                className="flex-1 educator-button px-6 py-3 rounded-lg text-white font-semibold disabled:opacity-50"
                data-testid="submit-feedback-btn"
              >
                {submitting ? "Submitting..." : "Submit Feedback"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Feedback Modal */}
      {showBulkModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowBulkModal(false)}
        >
          <div
            className="bg-white rounded-2xl p-8 max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                Bulk Feedback
              </h2>
              <button
                onClick={() => setShowBulkModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
                data-testid="close-bulk-modal"
              >
                ×
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-800 text-sm">
                <strong>Note:</strong> This feedback will be added to {selectedAnswers.size} selected answer{selectedAnswers.size > 1 ? "s" : ""}. 
                You can still edit individual feedback later.
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 font-semibold mb-2">Feedback for all selected answers:</label>
              <textarea
                value={bulkFeedback}
                onChange={(e) => setBulkFeedback(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500 min-h-[150px]"
                placeholder="Write feedback that applies to all selected answers..."
                data-testid="bulk-feedback-textarea"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowBulkModal(false)}
                className="flex-1 px-6 py-3 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
                data-testid="cancel-bulk-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkFeedback}
                disabled={submitting || !bulkFeedback.trim()}
                className="flex-1 educator-button px-6 py-3 rounded-lg text-white font-semibold disabled:opacity-50"
                data-testid="submit-bulk-btn"
              >
                {submitting ? "Submitting..." : `Add Feedback to ${selectedAnswers.size} Answer${selectedAnswers.size > 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeedbackDashboard;
